import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ModuleService } from '../../services/module.service';
import { Module, validateModuleName } from '../../model/module.model';
import { Subscription, firstValueFrom } from 'rxjs';
import { EntityService } from '../../services/entity.service';
import { StudioNavService } from '../../studio-nav/studio-nav.service';

@Component({
  selector: 'app-module-list',
  imports: [CommonModule, FormsModule],
  templateUrl: './module-list.component.html'
})
export class ModuleListComponent implements OnInit, OnDestroy {
  modules: Module[] = [];
  availableModules: Module[] = [];
  selectedModule: Module | null = null;
  showSetup: boolean = false;
  isInstalling: boolean = false;
  isConverting: boolean = false;
  searchQuery: string = '';
  showConvertModal: boolean = false;
  convertModuleName: string = '';
  convertModuleDescription: string = '';
  convertModuleImageUrl: string = '';
  selectedImageFile: File | null = null;
  nameValidationError: string = '';
  isLoadingModuleDetails: boolean = false;
  moduleEntities: any[] = [];
  moduleOotbEntities: any[] = [];
  modulePages: any[] = [];
  moduleRoles: any[] = [];
  functionsChecked: boolean = false;
  functionsToMove: any[] = [];
  functionsToMoveToArchive: any[] = [];
  isCheckingFunctions: boolean = false;
  
  // Selection tracking
  importOotb: boolean = false;
  selectedOotbEntityIds: Set<string> = new Set();
  selectedEntityIds: Set<string> = new Set();
  selectedPageIds: Set<string> = new Set();
  selectedRoleIds: Set<string> = new Set();
  selectedFunctionsToArchiveIds: Set<string> = new Set();

  constructor(private moduleService: ModuleService , private entityService: EntityService, private nav: StudioNavService) {}

  /** Nav 7b final review (I7) — the navigator's note listener; released with the screen. */
  private navSub: Subscription | null = null;

  async ngOnInit() {
    // "Open the WhatsApp module" while this list is already showing: no load comes to take the note, so this does —
    // once the list has that module. A note that arrives with the screen is taken by loadModules(), as before.
    this.navSub = this.nav.intent$.subscribe((i) => {
      if (i?.kind !== 'module') return;
      const found = this.modules.find((x) => x.id === i.id);
      if (!found) return;
      this.nav.take('module');
      this.selectModule(found);
    });
    await this.loadModules();
  }

  ngOnDestroy() {
    this.navSub?.unsubscribe();
  }

  async loadModules() {
    try {
      this.modules = await firstValueFrom(this.moduleService.$values);
      // Nav 7b — the note left by `go()` naming a module to open, once the list has it (spec Q1).
      const m = this.nav.take('module');
      if (m?.kind === 'module') { const found = this.modules.find((x) => x.id === m.id); if (found) this.selectModule(found); }
    } catch (error) {
      console.error('Error loading modules:', error);
    }
  }

  selectModule(module: Module) {
    this.selectedModule = module;
    this.showSetup = false;
  }

  openSetup() {
    this.showSetup = true;
    this.resetSelections();
    if (this.selectedModule) {
      this.loadModuleDetails(this.selectedModule.id);
    }
  }

  closeSetup() {
    this.showSetup = false;
    this.selectedModule = null;
  }

  resetSelections() {
    this.importOotb = false;
    this.selectedOotbEntityIds.clear();
    this.selectedEntityIds.clear();
    this.selectedPageIds.clear();
    this.selectedRoleIds.clear();
  }

  async loadModuleDetails(moduleId: string) {
    this.isLoadingModuleDetails = true;
    try {
      this.moduleEntities = await this.moduleService.getModuleEntities(moduleId);
      this.moduleOotbEntities = await this.moduleService.getModuleOotbEntities(moduleId);
      this.modulePages = await this.moduleService.getModulePages(moduleId);
      this.moduleRoles = await this.moduleService.getModuleRoles(moduleId);
    } catch (error) {
      console.error('Error loading module details:', error);
    } finally {
      this.isLoadingModuleDetails = false;
    }
  }

  toggleOotbEntitySelection(entityId: string) {
    if (this.selectedOotbEntityIds.has(entityId)) {
      this.selectedOotbEntityIds.delete(entityId);
    } else {
      this.selectedOotbEntityIds.add(entityId);
    }
  }

  selectAllOotbEntities() {
    this.moduleOotbEntities.forEach(e => this.selectedOotbEntityIds.add(e.id));
  }

  toggleEntitySelection(entityId: string) {
    if (this.selectedEntityIds.has(entityId)) {
      this.selectedEntityIds.delete(entityId);
    } else {
      this.selectedEntityIds.add(entityId);
    }
  }

  togglePageSelection(pageId: string) {
    if (this.selectedPageIds.has(pageId)) {
      this.selectedPageIds.delete(pageId);
    } else {
      this.selectedPageIds.add(pageId);
    }
  }

  toggleRoleSelection(roleId: string) {
    if (this.selectedRoleIds.has(roleId)) {
      this.selectedRoleIds.delete(roleId);
    } else {
      this.selectedRoleIds.add(roleId);
    }
  }

  selectAllEntities() {
    this.moduleEntities.forEach(e => this.selectedEntityIds.add(e.id));
  }

  selectAllPages() {
    this.modulePages.forEach(p => this.selectedPageIds.add(p.id));
  }

  get allOotbEntitiesSelected(): boolean {
    return this.moduleOotbEntities.length > 0 && 
           this.selectedOotbEntityIds.size === this.moduleOotbEntities.length;
  }

  get canSelectEntities(): boolean {
    return this.allOotbEntitiesSelected;
  }

  get allEntitiesSelected(): boolean {
    return this.moduleEntities.length > 0 && 
           this.selectedEntityIds.size === this.moduleEntities.length;
  }

  get canImportPages(): boolean {
    return this.allOotbEntitiesSelected && this.allEntitiesSelected;
  }

  async installModuleWithSelections() {
    if (!this.selectedModule) return;
    
    const summary = [];
    if (this.selectedOotbEntityIds.size > 0) summary.push(`${this.selectedOotbEntityIds.size} OOTB entities`);
    if (this.selectedEntityIds.size > 0) summary.push(`${this.selectedEntityIds.size} entities`);
    if (this.selectedPageIds.size > 0) summary.push(`${this.selectedPageIds.size} pages`);
    
    if (this.selectedOotbEntityIds.size === 0) {
      alert('Please select all OOTB entities to proceed');
      return;
    }
    
    if (confirm(`Import ${summary.join(', ')} to the current client?`)) {
      this.isInstalling = true;
      try {
        await this.moduleService.installModule(this.selectedModule, {
          importOotb: false,
          selectedOotbEntityIds: Array.from(this.selectedOotbEntityIds),
          selectedEntityIds: Array.from(this.selectedEntityIds),
          selectedPageIds: Array.from(this.selectedPageIds),
          selectedRoleIds: Array.from(this.selectedRoleIds)
        });
        alert('Module items imported successfully!');
        this.closeSetup();
        await this.loadModules();
      } catch (error) {
        console.error('Error importing module items:', error);
        alert('Failed to import. Please try again.');
      } finally {
        this.isInstalling = false;
      }
    }
  }

  async installModule(module: Module) {
    if (confirm(`Are you sure you want to install the ${module.name} module? This will create:\n- ${module.entityCount} entities\n- ${module.ootbEntityCount} OOTB entities\n- ${module.pageCount} pages\n- ${module.roleCount} roles`)) {
      this.isInstalling = true;
      try {
        await this.moduleService.installModule(module, {
          importOotb: true,
          selectedEntityIds: [],
          selectedPageIds: [],
          selectedRoleIds: []
        });
        alert(`${module.name} module installed successfully!`);
        await this.loadModules();
      } catch (error) {
        console.error('Error installing module:', error);
        alert('Failed to install module. Please try again.');
      } finally {
        this.isInstalling = false;
      }
    }
  }


  isModuleInstalled(moduleId: string): boolean {
    return this.modules.some(m => m.id === moduleId);
  }

  get filteredAvailableModules() {
    if (!this.searchQuery) return this.availableModules;
    return this.availableModules.filter(m => 
      m.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
      m.description.toLowerCase().includes(this.searchQuery.toLowerCase())
    );
  }

  getCategoryColor(category: string): string {
    const colors: any = {
      'CRM': 'bg-blue-500',
      'HR': 'bg-green-500',
      'FINANCE': 'bg-yellow-500',
      'INVENTORY': 'bg-purple-500',
      'CUSTOM': 'bg-gray-500'
    };
    return colors[category] || 'bg-gray-500';
  }

  openConvertModal() {
    this.showConvertModal = true;
    this.convertModuleName = '';
    this.convertModuleDescription = '';
    this.convertModuleImageUrl = '';
    this.selectedImageFile = null;
    this.nameValidationError = '';
    this.functionsChecked = false;
    this.functionsToMove = [];
    this.functionsToMoveToArchive = [];
    this.selectedFunctionsToArchiveIds.clear();
    this.checkFunctions();
  }

  closeConvertModal() {
    this.showConvertModal = false;
    this.functionsChecked = false;
    this.functionsToMove = [];
    this.functionsToMoveToArchive = [];
    this.selectedFunctionsToArchiveIds.clear();
  }

  async checkFunctions() {
    this.isCheckingFunctions = true;
    try {
      // Call service method to get functions that will be moved
      const functions = await this.moduleService.getFunctionsToMove();
      
      // Separate functions: with entityReference vs without functionsToMove should only functions where all the entityRefrece are already a part of present client else ignore
      
      // Get entity IDs that are part of the current client
      const allClientEntities = await firstValueFrom(this.entityService.$values);
      const currentClientEntityIds = new Set(allClientEntities.map(e => e.id));
      
      // Filter functions where ALL entity references are part of the current client
      this.functionsToMove = functions.filter((f: any) => {
        const hasEntityReference = f.inputs?.some((input: any) => input.entityReference) || 
                   f.outputs?.some((output: any) => output.entityReference);
        
        if (!hasEntityReference) return false;
        
        // Get all entity references from inputs and outputs
        const allEntityRefs = [
          ...(f.inputs?.filter((input: any) => input.entityReference).map((input: any) => input.entityReference) || []),
          ...(f.outputs?.filter((output: any) => output.entityReference).map((output: any) => output.entityReference) || [])
        ];
        
        // Check if ALL entity references exist in current client
        return allEntityRefs.every(entityRef => currentClientEntityIds.has(entityRef));
      });
      this.functionsToMoveToArchive = functions.filter((f: any) => 
        !f.inputs?.some((input: any) => input.entityReference) && 
        !f.outputs?.some((output: any) => output.entityReference)
      );
      
      this.functionsChecked = true;
      
      if (functions.length === 0) {
        alert('No functions found to move.');
      } else {
        alert(`Found ${functions.length} function(s). ${this.functionsToMove.length} with entity reference, ${this.functionsToMoveToArchive.length} without reference (can be archived).`);
      }
    } catch (error) {
      console.error('Error checking functions:', error);
      alert('Failed to check functions. Please try again.');
    } finally {
      this.isCheckingFunctions = false;
    }
  }

  toggleFunctionArchiveSelection(functionId: string) {
    if (this.selectedFunctionsToArchiveIds.has(functionId)) {
      this.selectedFunctionsToArchiveIds.delete(functionId);
    } else {
      this.selectedFunctionsToArchiveIds.add(functionId);
    }
  }

  selectAllFunctionsToArchive() {
    this.functionsToMoveToArchive.forEach(f => this.selectedFunctionsToArchiveIds.add(f.id));
  }

  validateName() {
    const validation = validateModuleName(this.convertModuleName);
    this.nameValidationError = validation.error || '';
    return validation.valid;
  }

  onImageSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 0.5MB)
      if (file.size > 0.5 * 1024 * 1024) {
        alert('Image size must be less than 0.5MB');
        return;
      }
      
      this.selectedImageFile = file;
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.convertModuleImageUrl = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  async convertClientToModule() {
    if (!this.validateName()) {
      return;
    }

    if (confirm(`This will convert your current client configuration into a module named "${this.convertModuleName}". Continue?`)) {
      this.isConverting = true;
     // convert this selectedFunctionsToArchiveIds back to function objects
      const selectedFunctionsToArchive = [...this.functionsToMove ,...this.functionsToMoveToArchive.filter(f => this.selectedFunctionsToArchiveIds.has(f.id))];
      // Pass selected functions to archive through converClintToModule method 
      
      try {
        await this.moduleService.convertClientToModule(
          this.convertModuleName,
          this.convertModuleDescription,
          this.convertModuleImageUrl ,
          selectedFunctionsToArchive
        );
        alert('Client successfully converted to module!');
        await this.loadModules();
        this.closeConvertModal();
      } catch (error) {
        console.error('Error converting client to module:', error);
        alert('Failed to convert client to module. Please try again.');
      } finally {
        this.isConverting = false;
      }
    }
  }
}
